import { SlideshowSelectEvent } from '@theme/events';
import { onDocumentReady } from '@theme/utilities';

/**
 * @typedef {Object} ThemeTranslations
 * @property {string} [mute_video]
 * @property {string} [unmute_video]
 */

/**
 * @typedef {Object} Theme
 * @property {ThemeTranslations} [translations]
 */

/**
 * @type {Window & { Theme?: Theme }}
 */
const _window = window;

const translations = {
  mute: _window.Theme?.translations?.mute_video ?? 'Mute video',
  unmute: _window.Theme?.translations?.unmute_video ?? 'Unmute video',
};

class HeroVideoController {
    // Track mute state independently to avoid logic errors when video.muted is out of sync.
    _isMuted = true;
    // Track if video just returned to view
    _justReturnedToView = false;
  /**
   * @param {HTMLElement} element
   */
  constructor(element) {
    this.element = element;
    this.button = element.querySelector('[data-hero-mute-toggle]');
    this.slideshow = element.querySelector('slideshow-component');
    this.isSlideshow = this.slideshow instanceof HTMLElement;
    this.intersectionObserver = undefined;
    /** @type {HTMLVideoElement[]} */
    this.videos = [];

    this.refreshVideos();

    if (!this.button) {
      return;
    }

    this.button.addEventListener('click', this.handleToggle);
    this._isMuted = true;
    this.updateButton(this._isMuted, { hasVideos: this.videos.length > 0 });

    if (this.isSlideshow) {
      this.element.addEventListener(SlideshowSelectEvent.eventName, this.handleSlideChange);
      this.handleSlideChange();
    }

    if ('IntersectionObserver' in window) {
      this.intersectionObserver = new IntersectionObserver(this.handleIntersection, {
        threshold: 0.25,
      });

      this.intersectionObserver.observe(this.element);
    }
    // Listen for intersection re-entry
    this._wasIntersecting = true;
  }

  refreshVideos() {
    this.videos = Array.from(this.element.querySelectorAll('video'));
  }

  getActiveVideos() {
    if (!this.isSlideshow || !this.slideshow) {
      return this.videos;
    }

    const activeSlides = Array.from(this.slideshow.querySelectorAll("slideshow-slide[aria-hidden='false']"));
    const slidesToCheck = activeSlides.length > 0 ? activeSlides : (() => {
      const fallback = this.slideshow.querySelector('slideshow-slide');
      return fallback ? [fallback] : [];
    })();

    const videos = [];
    for (const slide of slidesToCheck) {
      videos.push(...slide.querySelectorAll('video'));
    }

    return videos;
  }

  handleToggle = () => {
    this.refreshVideos();
    const targetVideos = this.isSlideshow ? this.getActiveVideos() : this.videos;

    console.log('[HeroVideo] Toggle button clicked. Target videos:', targetVideos);

    if (targetVideos.length === 0) {
      return;
    }

    if (this._justReturnedToView) {
      this._isMuted = false;
      this._justReturnedToView = false;
      this.setMuted(false, { videos: targetVideos });
      return;
    }

    this._isMuted = !this._isMuted;
    this.setMuted(this._isMuted, { videos: targetVideos });
  };

  handleSlideChange = () => {
    if (!this.isSlideshow) {
      return;
    }

    this.refreshVideos();

    const activeVideos = this.getActiveVideos();
    const inactiveVideos = this.videos.filter((video) => !activeVideos.includes(video));

    for (const video of inactiveVideos) {
      video.muted = true;
      video.pause?.();
    }

    // Always mute and play active videos for autoplay compliance
    for (const video of activeVideos) {
      video.muted = true;
      video.setAttribute('muted', '');
      this.playVideo(video);
    }

    this.setMuted(true, {
      videos: activeVideos,
      hasVideosOverride: activeVideos.length > 0,
    });
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  handleIntersection = (entries) => {
    const [entry] = entries;

    if (entry) {
      if (!entry.isIntersecting) {
        this.refreshVideos();
        const hasActive = this.isSlideshow ? this.getActiveVideos().length > 0 : this.videos.length > 0;
        this.setMuted(true, { videos: this.videos, hasVideosOverride: hasActive });
        this._wasIntersecting = false;
      } else if (!this._wasIntersecting) {
        // Video just came back into view
        this._justReturnedToView = true;
        this._wasIntersecting = true;
      }
    }
  };

  /**
   * @param {HTMLVideoElement} video
   */
  playVideo(video) {
    const playPromise = video.play?.();
    if (playPromise && typeof playPromise.then === 'function') {
      playPromise.catch((err) => {
        console.warn('[HeroVideo] playVideo error:', err);
      });
    }
  }

  /**
   * @param {boolean} muted
   * @param {{ videos?: HTMLVideoElement[]; hasVideosOverride?: boolean }} [options]
   */

setMuted(muted, { videos = this.isSlideshow ? this.getActiveVideos() : this.videos, hasVideosOverride } = {}) {
  const handled = new Set();

  for (const video of videos) {
    handled.add(video);
    video.muted = muted;
    if (muted) {
      video.setAttribute('muted', '');
    } else {
      video.removeAttribute('muted');
    }

    if (!muted) {
      this.playVideo(video);
    }
  }

  if (this.isSlideshow) {
    for (const video of this.videos) {
      if (!handled.has(video)) {
        video.muted = true;
        video.setAttribute('muted', '');
      }
    }
  }

  const hasVideos = hasVideosOverride ?? videos.length > 0;
  this.updateButton(muted, { hasVideos });
}


  /**
   * @param {boolean} muted
   * @param {{ hasVideos?: boolean }} [options]
   */
  updateButton(muted, { hasVideos = true } = {}) {
    if (!this.button) {
      console.warn('[HeroVideo] Mute/unmute button not found.');
      return;
    }

    if (!hasVideos) {
      /** @type {HTMLButtonElement} */ (this.button).hidden = true;
      const label = translations.unmute;
      this.button.textContent = label;
      this.button.setAttribute('aria-label', label);
      this.button.setAttribute('aria-pressed', 'false');
      return;
    }

    /** @type {HTMLButtonElement} */ (this.button).hidden = false;

    const label = muted ? translations.unmute : translations.mute;
    this.button.textContent = label;
    this.button.setAttribute('aria-label', label);
    this.button.setAttribute('aria-pressed', muted ? 'false' : 'true');
  }
}

/**
 * @typedef {HTMLElement & { dataset: DOMStringMap }} HeroVideoElement
 */

/**
 * @param {Document | HTMLElement} root
 * @returns {void}
 */
function mount(root) {
  /** @type {NodeListOf<HeroVideoElement>} */
  // @ts-ignore
  const controllers = root.querySelectorAll?.('[data-hero-video-controller]') ?? [];

  for (const element of controllers) {
    if (element.dataset.heroVideoBound === 'true') continue;
    element.dataset.heroVideoBound = 'true';
    new HeroVideoController(element);
  }
}

onDocumentReady(() => {
  mount(document);
});

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) {
    mount(event.target);
  }
});

document.addEventListener('shopify:section:select', (event) => {
  if (event.target instanceof HTMLElement) {
    mount(event.target);
  }
});
